import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PreviewPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId, planData, reportSource } = location.state || {};
  const [betaCode, setBetaCode] = useState('');
  const [betaError, setBetaError] = useState('');
  const [betaLoading, setBetaLoading] = useState(false);
  const [showBetaInput, setShowBetaInput] = useState(false);

  if (!sessionId || !planData) {
    navigate('/');
    return null;
  }

  // Extract first action for unlocked preview
  const firstAction = planData.one_thing_today || planData.actions?.[0] || null;
  const accounts = planData.accounts_extracted || planData.accounts || [];
  const timeline = planData.timeline_months || planData.preview?.timeline_months || 6;
  const goalScore = planData.goal_score || planData.preview?.goal_score || 720;
  const currentScore = planData.current_score || 580;
  const userName = planData.user_name || localStorage.getItem('userName') || '';
  
  // Check if non-Experian report
  const isNonExperian = reportSource && reportSource !== 'experian';

  const handleUnlock = () => {
    navigate('/checkout', { 
      state: { 
        sessionId, 
        timeline,
        reportSource
      } 
    });
  };

  const handleBetaUnlock = async () => {
    if (!betaCode.trim()) {
      setBetaError('Please enter a beta code');
      return;
    }
    
    setBetaLoading(true);
    setBetaError('');
    
    try {
      const response = await axios.post(`${API}/beta-unlock`, {
        session_id: sessionId,
        beta_code: betaCode.trim()
      });
      
      if (response.data.success) {
        localStorage.setItem('creditSessionId', sessionId);
        navigate('/upsell', { state: { sessionId, fromBeta: true, planData } });
      }
    } catch (error) {
      setBetaError(error.response?.data?.detail || 'Invalid beta code');
    } finally {
      setBetaLoading(false);
    }
  };

  // Extract details for first action display
  const getFirstActionDisplay = () => {
    if (firstAction?.action) {
      // one_thing_today format
      return {
        amount: firstAction.action.match(/\$[\d,]+/)?.[0] || '$50',
        account: firstAction.action.match(/→\s*(.+)/)?.[1] || 'Credit Card',
        utilization: firstAction.from_to || '80% → 30%',
        impact: firstAction.score_impact || '+10-20 points'
      };
    }
    if (accounts[0]) {
      const acc = accounts[0];
      return {
        amount: `$${Math.round(acc.balance * 0.5) || 50}`,
        account: `${acc.name || 'Credit Card'} ••${acc.last4 || '****'}`,
        utilization: `${acc.utilization || 80}% → 30%`,
        impact: '+10-20 points'
      };
    }
    return {
      amount: '$50',
      account: 'Credit Card ••7710',
      utilization: '81% → 30%',
      impact: '+10-16 points'
    };
  };

  const firstActionDisplay = getFirstActionDisplay();

  return (
    <div className="preview-container">
      <div className="preview-content">
        {/* Header */}
        <div className="preview-header">
          <h1 className="preview-title" data-testid="preview-title">
            {userName ? `${userName}, Your Plan is Ready` : 'Your Plan is Ready'}
          </h1>
          <p className="preview-subtitle">
            Here's a preview of your personalized credit action plan
          </p>
        </div>

        {/* Trust Badge */}
        <div className="trust-badge-preview">
          <span className="trust-check">✔</span>
          <span>Based on your actual credit report data</span>
        </div>

        {/* Non-Experian Warning */}
        {isNonExperian && (
          <div className="source-warning">
            <span className="warning-icon">⚠️</span>
            <p>This report format may have limited data. For maximum accuracy, consider using a free Experian report.</p>
          </div>
        )}

        {/* Score Summary */}
        <div className="score-summary">
          <div className="score-item">
            <span className="score-label">Current</span>
            <span className="score-value current">{currentScore}</span>
          </div>
          <div className="score-arrow">→</div>
          <div className="score-item">
            <span className="score-label">Goal</span>
            <span className="score-value goal">{goalScore}</span>
          </div>
          <div className="timeline-badge">
            {timeline} months
          </div>
        </div>

        {/* UNLOCKED: First Action Card */}
        <div className="action-card unlocked" data-testid="first-action-unlocked">
          <div className="action-card-header">
            <span className="action-badge">YOUR FIRST MOVE</span>
            <span className="unlocked-badge">UNLOCKED</span>
          </div>
          <div className="action-card-body">
            <div className="payment-row">
              <span className="pay-label">Pay</span>
              <span className="pay-amount">{firstActionDisplay.amount}</span>
              <span className="pay-arrow">→</span>
              <span className="pay-account">{firstActionDisplay.account}</span>
            </div>
            <div className="impact-row">
              <div className="impact-item">
                <span className="impact-value utilization">{firstActionDisplay.utilization}</span>
                <span className="impact-label">UTILIZATION</span>
              </div>
              <div className="impact-item">
                <span className="impact-value points">{firstActionDisplay.impact}</span>
                <span className="impact-label">HIGH IMPACT</span>
              </div>
            </div>
          </div>
        </div>

        {/* LOCKED: Remaining Actions */}
        <div className="locked-preview-section">
          <div className="action-card locked" data-testid="step-2-locked">
            <div className="lock-overlay">
              <span className="lock-icon">🔒</span>
            </div>
            <div className="action-card-header">
              <span className="action-badge">STEP 2</span>
            </div>
            <div className="action-card-body blurred">
              <div className="payment-row">
                <span className="pay-label">Pay</span>
                <span className="pay-amount">$XXX</span>
                <span className="pay-arrow">→</span>
                <span className="pay-account">Account ••XXXX</span>
              </div>
              <div className="impact-row">
                <div className="impact-item">
                  <span className="impact-value">XX% → XX%</span>
                  <span className="impact-label">UTILIZATION</span>
                </div>
              </div>
            </div>
          </div>

          <div className="action-card locked" data-testid="step-3-locked">
            <div className="lock-overlay">
              <span className="lock-icon">🔒</span>
            </div>
            <div className="action-card-header">
              <span className="action-badge">STEP 3</span>
            </div>
            <div className="action-card-body blurred">
              <div className="payment-row">
                <span className="pay-label">Pay</span>
                <span className="pay-amount">$XXX</span>
                <span className="pay-arrow">→</span>
                <span className="pay-account">Account ••XXXX</span>
              </div>
            </div>
          </div>

          <div className="action-card locked timeline-card" data-testid="timeline-locked">
            <div className="lock-overlay">
              <span className="lock-icon">🔒</span>
            </div>
            <div className="action-card-header">
              <span className="action-badge">TIMELINE & PROJECTION</span>
            </div>
            <div className="action-card-body blurred">
              <div className="timeline-preview">
                <span>Month 1-3: Foundation Phase</span>
                <span>Month 4-6: Acceleration Phase</span>
                <span>Score Projection: XXX → XXX</span>
              </div>
            </div>
          </div>
        </div>

        {/* Unlock Section */}
        <div className="unlock-section">
          <h2 className="unlock-title">Unlock Your Full Credit Action Plan</h2>
          <p className="unlock-subtitle-text">
            You now know what's holding your score back. Unlock your full step-by-step plan to fix it.
          </p>
          
          <div className="unlock-price-box">
            <span className="price-amount" data-testid="unlock-price">$29.99</span>
            <span className="price-label">one-time payment</span>
          </div>
          
          <div className="value-list">
            <div className="value-item">
              <span className="value-check">✓</span>
              <span>Exact payment amounts for each account</span>
            </div>
            <div className="value-item">
              <span className="value-check">✓</span>
              <span>Priority-ranked actions (what to pay first)</span>
            </div>
            <div className="value-item">
              <span className="value-check">✓</span>
              <span>Timeline strategy for your {timeline}-month goal</span>
            </div>
            <div className="value-item">
              <span className="value-check">✓</span>
              <span>Score improvement projections</span>
            </div>
            <div className="value-item">
              <span className="value-check">✓</span>
              <span>Downloadable PDF action plan</span>
            </div>
          </div>

          <button 
            data-testid="unlock-plan-btn"
            className="unlock-btn" 
            onClick={handleUnlock}
          >
            Get My Full Plan
          </button>
          
          <div className="unlock-trust">
            <span>🔒</span> Secure payment powered by Stripe • No subscriptions
          </div>

          {/* Beta Code Section */}
          <div className="beta-code-section">
            {!showBetaInput ? (
              <button 
                className="beta-toggle-btn"
                onClick={() => setShowBetaInput(true)}
                data-testid="show-beta-input-btn"
              >
                Have a beta access code?
              </button>
            ) : (
              <div className="beta-input-container">
                <input
                  type="text"
                  placeholder="Enter beta code"
                  value={betaCode}
                  onChange={(e) => setBetaCode(e.target.value.toUpperCase())}
                  className="beta-input"
                  data-testid="beta-code-input"
                />
                <button
                  onClick={handleBetaUnlock}
                  disabled={betaLoading}
                  className="beta-submit-btn"
                  data-testid="beta-submit-btn"
                >
                  {betaLoading ? 'Verifying...' : 'Apply Code'}
                </button>
                {betaError && <p className="beta-error">{betaError}</p>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewPage;
